// The Workflows run API (`/api/v4/workflows`, backend/web/src/workflows-v4/api/run.controller.ts)
// and its event stream. Ported from packages/workflow-engine-upstream/ui/src/api.ts (bebc382): the
// same methods over our authenticated client instead of the demo host's open routes, without the
// demo's kit cases. Every call goes through `apiService.fetchOrError`, which takes a fresh bearer
// token per request, so a stream reopened after a drop never reuses an expired token.
import { apiService } from '@/service/api';
import { readSSEMessages } from '@/utils/sse';
import type { Conversation, Period, Plan, Progress, RunListItem, RunState } from './contracts';

export type * from './contracts/run-state';

const BASE = 'v4/workflows';
const runPath = (runId: string, suffix = '') => `${BASE}/runs/${encodeURIComponent(runId)}${suffix}`;

async function post<T>(path: string, body: object, opts?: { timeoutMs?: number | null }): Promise<T> {
  const res = await apiService.fetchOrError(
    apiService.getUrl(path),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
    opts,
  );
  return (await res.json()) as T;
}

/** Why the page cannot run workflows at all, from the API's 409 and 503 refusals. */
export type Unavailable = { kind: 'not_available'; reason: string } | { kind: 'warming'; code: string };

/** The 409 `workflows_not_available` (with its reason) or a 503 KG warm-up refusal; null for any other error. */
export function unavailableOf(error: unknown): Unavailable | null {
  const e = error as { response?: { status?: number }; body?: { error?: unknown; reason?: unknown } } | null;
  const status = e?.response?.status;
  const code = typeof e?.body?.error === 'string' ? e.body.error : undefined;
  if (status === 409 && code === 'workflows_not_available') {
    return { kind: 'not_available', reason: typeof e?.body?.reason === 'string' ? e.body.reason : '' };
  }
  if (status === 503) return { kind: 'warming', code: code ?? 'unavailable' };
  return null;
}

/** The API answered 404: the run is gone, or it is another organization's (the tenant store has no such run). */
export function isNotFound(error: unknown): boolean {
  return (error as { response?: { status?: number } } | null)?.response?.status === 404;
}

/** A 4xx other than 408/429 will not improve on retry (a 404 is a run gone, or another org's). */
function retryable(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return typeof status !== 'number' || status >= 500 || status === 408 || status === 429;
}

const RETRY_MS = 3_000;
/** Silence that counts as a dead socket: the server beats every 15 s. */
const INACTIVITY_MS = 60_000;

export const api = {
  /** A question, on its own or as a follow-up carrying the conversation it continues. */
  start: (question: string, conversation?: Conversation) =>
    post<{ runIds: string[] }>(`${BASE}/runs`, conversation ? { question, conversation } : { question }),
  /** The organization's runs, newest activity first (at most `limit`, the API's cap being 200). */
  list: async (limit?: number) =>
    (await apiService.fetchOrError(apiService.getUrl(`${BASE}/runs`, limit === undefined ? undefined : { limit }))).json() as Promise<{ runs: RunListItem[] }>,
  /** `calls: 'summary'`: an admin's calls without their input and with only the output's outcome and reason. */
  state: async (runId: string, calls?: 'summary') =>
    (await apiService.fetchOrError(apiService.getUrl(runPath(runId), calls ? { calls } : undefined))).json() as Promise<RunState>,
  progress: async (runId: string) => (await apiService.fetchOrError(apiService.getUrl(runPath(runId, '/progress')))).json() as Promise<Progress>,
  sendFile: (runId: string, file: MethodFile) => post(runPath(runId, '/files'), file),
  // The engine test-reads an edited plan on real data before it accepts or refuses it, which can
  // take longer than the default 45 s; the editor stays open until that verdict.
  edit: (runId: string, plan: Plan) => post(runPath(runId, '/plan'), { plan }, { timeoutMs: null }),
  approve: (runId: string, planVersion: number) => post(runPath(runId, '/approve'), { planVersion }),
  cancel: (runId: string) => post(runPath(runId, '/cancel'), {}),
  /** Soft-deletes the run: it leaves the library's list. */
  delete: async (runId: string) => {
    await apiService.fetchOrError(apiService.getUrl(runPath(runId)), { method: 'DELETE' });
  },
  rerun: (runId: string, period: Period) => post<{ runIds: string[] }>(runPath(runId, '/rerun'), { period }),
  answer: (resumeToken: string, values: Record<string, string>) => post(`${BASE}/asks/answer`, { resumeToken, values }),
  /**
   * Calls back on every change of the run, with its type (our stream sends only `event`); returns a
   * function that stops listening. Like the EventSource it replaces, it reopens the stream after a
   * drop (and calls back once on reopening, as `state`, so an event missed in between still
   * reloads the run).
   */
  watch(runId: string, onChange: (type: string) => void): () => void {
    const controller = new AbortController();
    const { signal } = controller;
    void (async () => {
      let opened = false;
      while (!signal.aborted) {
        try {
          const res = await apiService.fetchOrError(
            apiService.getUrl(runPath(runId, '/stream')),
            { headers: { Accept: 'text/event-stream' }, signal },
            { timeoutMs: null },
          );
          if (opened) onChange('state');
          opened = true;
          for await (const message of readSSEMessages(res, { inactivityTimeoutMs: INACTIVITY_MS })) {
            if (signal.aborted) return;
            if (message.data) onChange((JSON.parse(message.data) as { type?: string }).type ?? '');
          }
        } catch (error) {
          if (signal.aborted || !retryable(error)) return;
        }
        await new Promise<void>((resolve) => {
          const timer = setTimeout(resolve, RETRY_MS);
          signal.addEventListener('abort', () => {
            clearTimeout(timer);
            resolve();
          });
        });
      }
    })();
    return () => controller.abort();
  },
};

export interface MethodFile {
  kind: 'file';
  title: string;
  mediaType: 'text/markdown' | 'text/plain' | 'application/pdf';
  content: string;
}

export async function methodFile(file: File): Promise<MethodFile> {
  if (file.size > 7 * 1024 * 1024) throw new Error('The file exceeds 7 MB. Send a smaller file.');
  const extension = file.name.split('.').at(-1)?.toLowerCase();
  const mediaType = extension === 'pdf' ? 'application/pdf' : extension === 'md' || extension === 'markdown' ? 'text/markdown' : extension === 'txt' ? 'text/plain' : undefined;
  if (!mediaType) throw new Error('Send a PDF, Markdown, or plain text file.');
  let content: string;
  if (mediaType === 'application/pdf') {
    const bytes = new Uint8Array(await file.arrayBuffer());
    const chunks: string[] = [];
    for (let offset = 0; offset < bytes.length; offset += 8192) chunks.push(String.fromCharCode(...bytes.subarray(offset, offset + 8192)));
    content = btoa(chunks.join(''));
  } else content = await file.text();
  return { kind: 'file', title: file.name, mediaType, content };
}
